/**
 * App Context for global state management
 */

import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppState, AppStatus, Step } from '../types';

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

type AppAction =
  | { type: 'SET_TEST_CASE'; payload: string }
  | { type: 'SET_WEBSITE_URL'; payload: string }
  | { type: 'SET_ASSERTIONS'; payload: string[] }
  | { type: 'SET_SESSION_ID'; payload: string | null }
  | { type: 'SET_STEPS'; payload: Step[] }
  | { type: 'SET_FINAL_CODE'; payload: string | null }
  | { type: 'SET_STATUS'; payload: AppStatus }
  | { type: 'SET_EXECUTION_RESULT'; payload: any }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'UPDATE_STEP'; payload: { index: number; step: Partial<Step> } }
  | { type: 'SET_SELECTED_STEP_TAB'; payload: number | null }
  | { type: 'RESET' };

const initialState: AppState = {
  sessionId: null,
  testCase: '',
  websiteUrl: '',
  assertions: [],
  steps: [],
  finalCode: null,
  status: 'idle',
  executionResult: null,
  error: null,
  selectedStepTab: null,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_TEST_CASE':
      return { ...state, testCase: action.payload };
    case 'SET_WEBSITE_URL':
      return { ...state, websiteUrl: action.payload };
    case 'SET_ASSERTIONS':
      return { ...state, assertions: action.payload };
    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };
    case 'SET_STEPS':
      return { ...state, steps: action.payload };
    case 'SET_FINAL_CODE':
      return { ...state, finalCode: action.payload };
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_EXECUTION_RESULT':
      return { ...state, executionResult: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'UPDATE_STEP':
      const updatedSteps = [...state.steps];
      updatedSteps[action.payload.index] = {
        ...updatedSteps[action.payload.index],
        ...action.payload.step,
      };
      return { ...state, steps: updatedSteps };
    case 'SET_SELECTED_STEP_TAB':
      return { ...state, selectedStepTab: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
